/**
 * Auth stuff for the app
 */
import jwt_decode from 'jwt-decode';
import Util from './util';
import { b64_hmac_sha1 } from './helper';

interface DecodedJWT {
  exp: number;
}

const Auth = {
  /**
   * DEV PURPOSE
   * =================
   * Function to check if cookies has been set. This function is used for DEV purpose only
   * On localhost:3000 cookies are not set, so we are proxying the requests
   * to get around CORS for local development
   * using https://www.npmjs.com/package/local-cors-proxy
   */
  isUserAuthenticated(): boolean {
    return (
      !!Util.getCookie('TOKEN_ID')
        && !!Util.getCookie('TOKEN_SECRET')
        && !!Util.getCookie('ACCOUNT_ID')
    );
  },

  /**
   * Function to get AWS Auth String that is added to Authorization headers
   * 'Authorization': awsAuthToken
   * 'X-Amz-Date': [date used to create awsAuthToken]
   *
   * @param dateString date string in format yyyy-MM-dd'T'HH:mm:ss.SSS'Z'
   * @param handlerActionClass optional action class for a handler
   */
  getAWSAuthString(
    dateString: string,
    handlerActionClass?: string,
  ): string | null {
    const tokenId = decodeURIComponent(Util.getCookie('TOKEN_ID')!);
    const tokenSecret = decodeURIComponent(Util.getCookie('TOKEN_SECRET')!);
    const signatureParts = ['POST', '', '', dateString];
    if (handlerActionClass) {
      signatureParts.push(handlerActionClass);
    }
    const signature = signatureParts.join('\n');

    const awsAuthToken = `AWS ${tokenId}:${b64_hmac_sha1(
      tokenSecret,
      signature,
    )}`;

    return awsAuthToken;
  },

  /**
   * Function that returns dateString used in conjunction with awsAuthString.
   * This is either used in payload for handlers or in the headers (X-Amz-Date).
   * Date returned in this format: yyyy-MM-dd'T'HH:mm:ss.SSS'Z'
   *
   * @param dateTime optional Date object
   */
  getAWSAuthDateString(dateTime?: Date): string {
    const date = dateTime || new Date();

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Month is 0-based
    const day = String(date.getDate()).padStart(2, '0');

    const hour = String(date.getHours()).padStart(2, '0');
    const minute = String(date.getMinutes()).padStart(2, '0');
    const second = String(date.getSeconds()).padStart(2, '0');
    const timezoneOffset = String(date.toString().split(' ')[5].slice(3, 9));

    const strDate = `${year}-${month}-${day} ${hour}:${minute}:${second} ${timezoneOffset}`;
    return strDate;
  },

  /**
   * Function that returns a promise to obtain JWT token
   */
  obtainJWTToken(): Promise<any> {
    const dateString = this.getAWSAuthDateString();
    const awsAuthString = this.getAWSAuthString(dateString)!;
    const accountId = Util.getCookie('ACCOUNT_ID');
    const host = Util.getBackendHost();

    // create request headers
    const requestHeaders: HeadersInit = new Headers();
    requestHeaders.set('X-Requested-With', 'XMLHttpRequest');
    requestHeaders.set('Content-Type', 'text/plain');
    requestHeaders.set('Authorization', awsAuthString);
    requestHeaders.set('X-Amz-Date', dateString);

    return new Promise((resolve, reject) => {
      if (!accountId) {
        reject();
        return;
      }

      fetch(`${host}/auth/jwt/generate/${accountId}`, {
        method: 'GET',
        mode: 'cors',
        headers: requestHeaders,
      })
        .then((response) => {
          if (response.status !== 200) {
            reject(response);
          } else {
            resolve(response.text());
          }
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  isJwtValid(): boolean {
    const jwt = Util.getCookie('JWT');
    if (!jwt) {
      return false;
    }
    if (jwt) {
      const decoded = jwt_decode<DecodedJWT>(jwt);
      const BUFFER_TIME = 1000 * 10; // BUFFER TIME FOR 10 Secs.
      if (decoded && (Date.now() >= ((decoded.exp * 1000) - BUFFER_TIME)
          || ((decoded.exp * 1000) - Date.now()) < 120000)) {
        return false;
      }
    }
    return true;
  },
};

export default Auth;
